import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ErrorObject } from 'types';

export type CustomerArtifactItemState = {
  fileName: string;
  progress: number | null;
  status: 'pending' | 'presigning' | 'uploading' | 'done' | 'error';
  key?: string;
  errorMessage?: string;
};

export type CustomerArtifactUploadPhase = 'idle' | 'presigning' | 'uploading' | 'done';

export type FileUploadsState = {
  error: ErrorObject | null;
  progress: number | null;
  uploading: boolean;
  success: boolean;
  actualProvider: string | null;
  artifactPhase: CustomerArtifactUploadPhase;
  artifactPresignError: string | null;
  artifactItems: CustomerArtifactItemState[];
};

type FileUploadsReducer<P = void> = (state: FileUploadsState, action: PayloadAction<P>) => void;

const initialState: FileUploadsState = {
  error: null,
  progress: null,
  uploading: false,
  success: false,
  actualProvider: null,
  artifactPhase: 'idle',
  artifactPresignError: null,
  artifactItems: [],
};

type UploadFilePayload = {
  fileName?: string;
  uploadURL?: string;
  file: File;
  provider: 'o365' | 'zendesk';
};
const handleUploadFileS3: FileUploadsReducer<UploadFilePayload> = (state, action) => {
  state.progress = 0;
  state.uploading = true;
  state.success = false;
  state.actualProvider = action.payload.provider;
  state.artifactPhase = 'idle';
  state.artifactPresignError = null;
  state.artifactItems = [];
};

const handleUploadFileWithS3URL: FileUploadsReducer<Omit<UploadFilePayload, 'provider'>> = (
  state
) => {
  state.progress = 0;
  state.uploading = true;
  state.success = false;
  state.artifactPhase = 'idle';
  state.artifactPresignError = null;
  state.artifactItems = [];
};

type UploadFileProgressPayload = {
  progress: number;
};
const handleUploadFileS3Progress: FileUploadsReducer<UploadFileProgressPayload> = (
  state,
  action
) => {
  state.progress = action.payload.progress;
};

const handleUploadFileS3Success: FileUploadsReducer = (state) => {
  state.success = true;
};

type UloadFileS3FailurePayload = {
  error: ErrorObject;
};

const handleUploadFileS3Failure: FileUploadsReducer<UloadFileS3FailurePayload> = (
  state,
  action
) => {
  const { error } = action.payload;

  state.error = error;
};

const handleUploadFileS3Fulfill: FileUploadsReducer = (state) => {
  state.progress = null;
  state.uploading = false;
};

const handleUploadCustomerArtifacts: FileUploadsReducer<{ files: File[] }> = (state, action) => {
  state.artifactPhase = 'presigning';
  state.artifactPresignError = null;
  state.artifactItems = action.payload.files.map((f) => ({
    fileName: f.name,
    progress: null,
    status: 'presigning' as const,
  }));
  state.error = null;
  state.progress = null;
  state.uploading = false;
  state.success = false;
  state.actualProvider = null;
};

const handleCustomerArtifactPresignFailed: FileUploadsReducer<{ message: string }> = (
  state,
  action
) => {
  state.artifactPhase = 'idle';
  state.artifactPresignError = action.payload.message;
  state.artifactItems = [];
};

const handleCustomerArtifactPresignSucceeded: FileUploadsReducer = (state) => {
  state.artifactPhase = 'uploading';
  state.artifactItems = state.artifactItems.map((row) => ({
    ...row,
    status: 'pending',
  }));
};

const handleCustomerArtifactItemUploadStart: FileUploadsReducer<{ index: number; key: string }> = (
  state,
  action
) => {
  const row = state.artifactItems[action.payload.index];
  if (row != null) {
    row.status = 'uploading';
    row.progress = 0;
    row.key = action.payload.key;
  }
};

const handleCustomerArtifactItemProgress: FileUploadsReducer<{
  index: number;
  progress: number;
}> = (state, action) => {
  const row = state.artifactItems[action.payload.index];
  if (row != null) {
    row.progress = action.payload.progress;
  }
};

const handleCustomerArtifactItemDone: FileUploadsReducer<{ index: number; key: string }> = (
  state,
  action
) => {
  const row = state.artifactItems[action.payload.index];
  if (row != null) {
    row.status = 'done';
    row.progress = 100;
    row.key = action.payload.key;
  }
};

const handleCustomerArtifactItemFailed: FileUploadsReducer<{ index: number; message: string }> = (
  state,
  action
) => {
  const row = state.artifactItems[action.payload.index];
  if (row != null) {
    row.status = 'error';
    row.progress = null;
    row.errorMessage = action.payload.message;
  }
};

const handleCustomerArtifactUploadBatchFinished: FileUploadsReducer = (state) => {
  state.artifactPhase = 'done';
};

const handleResetCustomerArtifactUploads: FileUploadsReducer = (state) => {
  state.artifactPhase = 'idle';
  state.artifactPresignError = null;
  state.artifactItems = [];
};

const fileUploadsSlice = createSlice({
  name: 'fileUploads',
  initialState,
  reducers: {
    uploadFileS3: handleUploadFileS3,
    uploadFileWithS3URL: handleUploadFileWithS3URL,
    uploadFileS3Progress: handleUploadFileS3Progress,
    uploadFileS3Success: handleUploadFileS3Success,
    uploadFileS3Failure: handleUploadFileS3Failure,
    uploadFileS3Fulfill: handleUploadFileS3Fulfill,
    uploadCustomerArtifacts: handleUploadCustomerArtifacts,
    customerArtifactPresignFailed: handleCustomerArtifactPresignFailed,
    customerArtifactPresignSucceeded: handleCustomerArtifactPresignSucceeded,
    customerArtifactItemUploadStart: handleCustomerArtifactItemUploadStart,
    customerArtifactItemProgress: handleCustomerArtifactItemProgress,
    customerArtifactItemDone: handleCustomerArtifactItemDone,
    customerArtifactItemFailed: handleCustomerArtifactItemFailed,
    customerArtifactUploadBatchFinished: handleCustomerArtifactUploadBatchFinished,
    resetCustomerArtifactUploads: handleResetCustomerArtifactUploads,
  },
});

const { actions, reducer } = fileUploadsSlice;

export const {
  uploadFileS3,
  uploadFileWithS3URL,
  uploadFileS3Progress,
  uploadFileS3Success,
  uploadFileS3Failure,
  uploadFileS3Fulfill,
  uploadCustomerArtifacts,
  customerArtifactPresignFailed,
  customerArtifactPresignSucceeded,
  customerArtifactItemUploadStart,
  customerArtifactItemProgress,
  customerArtifactItemDone,
  customerArtifactItemFailed,
  customerArtifactUploadBatchFinished,
  resetCustomerArtifactUploads,
} = actions;

export default reducer;
